import React from 'react';
import { Link } from 'react-router-dom';
import {
  MessageCircleHeart, Camera, Bell, TriangleAlert, MapPin, Cat, Dog, Star,
  ArrowRight, Check, ShieldCheck, Sparkles, Users,
} from 'lucide-react';
import { BRAND, PARENT_PLAN, VET_PLANS, PAWPAL_URL, CAT_AVATARS } from '../mock';
import Footer from '../components/Footer';
import MarketingInstallCTA from '../components/MarketingInstallCTA';

const FEATURES = [
  { icon: MessageCircleHeart, title: 'AI Vet Chat', desc: 'Ask anything about your cat — food, behavior, symptoms — and get warm, instant, cats-focused guidance.' },
  { icon: Camera, title: 'Symptom Photo Analysis', desc: 'Upload a photo of eyes, skin, ears or wounds and get an instant read on possible causes and next steps.' },
  { icon: Bell, title: 'Care Reminders', desc: 'Never miss a vaccine or vet visit. PRO members get email & text alerts 7 days and again 3 days before.' },
  { icon: TriangleAlert, title: 'Emergency Detection', desc: 'Purrflect flags urgent situations and points you straight to the nearest emergency clinic.' },
  { icon: MapPin, title: 'Local Cat-Vet Finder', desc: 'Find trusted clinics with Featured badges, an emergency filter, and one-tap Call & Directions.' },
  { icon: Users, title: 'Multi-Pet Support', desc: 'Have more than one cat? Manage every whiskered family member from a single account.' },
];

const STEPS = [
  { n: '1', title: 'Add your cat', desc: 'Create a quick profile — name, breed, age and photo.' },
  { n: '2', title: 'Ask or upload', desc: 'Chat with the AI vet or upload a symptom photo for instant guidance.' },
  { n: '3', title: 'Stay on top of care', desc: 'Set reminders and find trusted local vets when you need them.' },
];

const Nav = () => (
  <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
      <a href="#top" className="flex items-center gap-2">
        <img src="/purrflect-icon.png" alt="Purrflect" className="h-8 w-8 rounded-xl object-cover" />
        <span className="text-lg font-extrabold tracking-tight text-[hsl(var(--coral))]">Purrflect</span>
      </a>
      <nav className="hidden items-center gap-7 text-sm font-semibold text-muted-foreground md:flex">
        <a href="#features" className="hover:text-foreground">Features</a>
        <a href="#how" className="hover:text-foreground">How it works</a>
        <a href="#pricing" className="hover:text-foreground">Pricing</a>
        <Link to="/vets" className="hover:text-foreground">For Vets</Link>
        <Link to="/beta" className="hover:text-foreground">Join Beta</Link>
      </nav>
      <Link to="/onboarding" className="coral-btn rounded-full px-5 py-2.5 text-sm font-bold text-white">Open the app</Link>
    </div>
  </header>
);

export default function MarketingHome() {
  return (
    <div id="top" className="min-h-screen scroll-smooth bg-background">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-24">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-[hsl(var(--coral))]">
              <Sparkles className="h-3.5 w-3.5" /> Free to try · PRO $9/mo
            </span>
            <h1 className="mt-4 text-5xl font-extrabold leading-[1.05] tracking-tight md:text-6xl">
              Your cat's wellbeing, in your pocket.
            </h1>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-muted-foreground">
              {BRAND.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/onboarding" className="coral-btn inline-flex items-center gap-2 rounded-2xl px-7 py-4 text-base font-bold text-white">
                Get started free <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/beta" className="rounded-2xl border border-border bg-white px-7 py-4 text-base font-bold hover:border-[hsl(var(--coral))]">
                Join the beta →
              </Link>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Cancel anytime · Stripe-secured · No card to try</p>
          </div>
          <div className="relative animate-fade-up">
            <div className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-accent/50 blur-2xl" />
            <img src={BRAND.heroImage} alt="Happy cat" className="h-[420px] w-full rounded-[2rem] object-cover shadow-xl" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-6xl px-5 py-16">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-extrabold tracking-tight md:text-4xl">Everything a cat parent needs</h2>
          <p className="mt-3 text-muted-foreground">Thoughtful, cats-only care — guidance, reminders and trusted local help in one warm app.</p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="card-soft rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-0.5 animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent"><f.icon className="h-6 w-6 text-[hsl(var(--coral))]" /></div>
              <h3 className="mt-4 text-lg font-bold">{f.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-3xl font-extrabold tracking-tight md:text-4xl">How it works</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-2xl bg-white p-6 card-soft">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--coral))] text-lg font-extrabold text-white">{s.n}</div>
                <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex -space-x-3">
            {CAT_AVATARS.slice(0, 5).map((a) => <img key={a} src={a} alt="cat" className="h-12 w-12 rounded-full border-2 border-background object-cover" />)}
            <span className="ml-5 self-center text-sm font-semibold text-muted-foreground">Loved by cat parents across our launch cities</span>
          </div>
        </div>
      </section>

      {/* Install nudge */}
      <MarketingInstallCTA />

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-3xl font-extrabold tracking-tight md:text-4xl">Simple, honest pricing</h2>
        <p className="mt-3 text-muted-foreground">Start free. Upgrade when you're ready. Cancel anytime.</p>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-white p-7 card-soft">
            <p className="font-bold">Free</p>
            <p className="mt-2 text-4xl font-extrabold">$0</p>
            <p className="mt-1 text-sm text-muted-foreground">To try it out</p>
            <ul className="mt-5 space-y-2 text-sm">
              {['3 AI interactions / month', 'Local vet finder', 'One cat profile'].map((x) => <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-[hsl(var(--coral))]" />{x}</li>)}
            </ul>
          </div>
          <div className="relative rounded-2xl border-2 border-[hsl(var(--coral))] bg-accent/30 p-7 card-soft">
            <span className="absolute -top-3 left-7 rounded-full bg-[hsl(var(--coral))] px-3 py-1 text-[11px] font-bold uppercase text-white">Most popular</span>
            <p className="font-bold">PRO</p>
            <p className="mt-2 text-4xl font-extrabold text-[hsl(var(--coral))]">${PARENT_PLAN.price}<span className="text-base text-muted-foreground">/mo</span></p>
            <p className="mt-1 text-sm text-muted-foreground">Unlimited care</p>
            <ul className="mt-5 space-y-2 text-sm">
              {PARENT_PLAN.perks.map((x) => <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-[hsl(var(--coral))]" />{x}</li>)}
            </ul>
            <Link to="/onboarding" className="coral-btn mt-6 block rounded-xl py-3 text-center font-bold text-white">Get PRO</Link>
          </div>
          <div className="rounded-2xl border border-border bg-white p-7 card-soft">
            <p className="font-bold">Purrflect + Wooflect</p>
            <p className="mt-2 text-4xl font-extrabold">${PARENT_PLAN.bundlePrice}<span className="text-base text-muted-foreground">/mo</span></p>
            <p className="mt-1 text-sm text-muted-foreground">Cats & dogs, bundled</p>
            <ul className="mt-5 space-y-2 text-sm">
              {['Everything in PRO', 'Both apps unlocked', 'One simple subscription'].map((x) => <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-[hsl(var(--coral))]" />{x}</li>)}
            </ul>
          </div>
        </div>
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4" /> Stripe-secured payments · cancel anytime</p>
      </section>

      {/* Vet CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-16">
        <div className="flex flex-col items-start gap-5 rounded-3xl bg-[hsl(var(--coral))] p-8 text-white md:flex-row md:items-center md:justify-between md:p-10">
          <div>
            <div className="flex items-center gap-2"><Star className="h-5 w-5" /><span className="text-sm font-bold uppercase tracking-wide">For veterinary clinics</span></div>
            <h3 className="mt-2 text-2xl font-extrabold md:text-3xl">Reach more cat parents.</h3>
            <p className="mt-1 max-w-lg text-white/90">Get featured, receive direct inbound calls, and grow your clinic. Plans from ${VET_PLANS[0].price}/mo.</p>
          </div>
          <Link to="/vets/join" className="rounded-2xl bg-white px-7 py-4 text-base font-bold text-[hsl(var(--coral))]">Become a founding partner</Link>
        </div>
      </section>

      {/* Cross-promo */}
      <section className="mx-auto max-w-6xl px-5 pb-16">
        <div className="flex items-center gap-4 rounded-2xl border border-border bg-white p-6 card-soft">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent"><Dog className="h-6 w-6 text-[hsl(var(--coral))]" /></div>
          <div className="flex-1">
            <p className="font-extrabold">Have a dog too?</p>
            <p className="text-sm text-muted-foreground">Meet <span className="font-semibold text-foreground">Wooflect</span>, our sister app for dogs — and save with the bundle.</p>
          </div>
          <a href={PAWPAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--coral))] px-5 py-2.5 text-sm font-bold text-[hsl(var(--coral))]">
            Visit Wooflect <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <div className="border-t border-border"><Footer /></div>
    </div>
  );
}
